import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ArrowRight, Pencil } from 'lucide-react';
import { AREE } from '../../constants/areas';

const fmtPad = (n) => String(n).padStart(2, '0');
const fmtNum = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n)
    ? n.toLocaleString('it-IT')
    : n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
  return unita ? `${s} ${unita}` : s;
};

/**
 * InlineKrValueEditor — editor inline del `valore_attuale` di un KR.
 * Renderizzato dentro un <Link>: usa stopPropagation + preventDefault per
 * evitare la navigazione al click sul bottone/input.
 * Editabile SOLO se il KR ha un target numerico (natura !== 'tappa' e
 * `target_committed` non-null): per i KR "tappa" ritorna solo il testo statico.
 */
function InlineKrValueEditor({ kr, onSave, meta }) {
  const isEditable = kr.natura !== 'tappa' && kr.target_committed !== null && kr.target_committed !== undefined;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(kr.valore_attuale ?? ''));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const cancel = () => { setDraft(String(kr.valore_attuale ?? '')); setEditing(false); };

  const submit = async () => {
    const raw = draft.trim().replace(',', '.');
    if (raw === '' || raw === String(kr.valore_attuale ?? '')) { setEditing(false); return; }
    const num = Number(raw);
    if (!Number.isFinite(num)) { cancel(); return; }
    setSaving(true);
    try {
      await onSave(kr.id, num);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!isEditable) {
    return (
      <span className="text-soft tabular-nums">
        {fmtNum(kr.valore_attuale, kr.unita)} / {fmtNum(kr.target_committed, kr.unita)}
      </span>
    );
  }

  if (editing) {
    return (
      <span
        className="tabular-nums inline-flex items-center gap-1"
        onClick={stop}
        onMouseDown={stop}
      >
        <input
          ref={inputRef}
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onBlur={submit}
          disabled={saving}
          className="w-24 px-1.5 py-0.5 border-b bg-canvas text-ink font-mono text-xs text-right focus:outline-none"
          style={{ borderColor: meta.color }}
          data-testid={`kr-value-input-${kr.id}`}
        />
        <span className="text-soft"> / {fmtNum(kr.target_committed, kr.unita)}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { stop(e); setEditing(true); }}
      onMouseDown={stop}
      className="text-soft tabular-nums inline-flex items-center gap-1.5 border-b border-dashed border-transparent hover:border-current hover:text-ink transition-colors group/edit"
      title="Clicca per modificare il valore attuale"
      data-testid={`kr-value-edit-${kr.id}`}
    >
      <span>{fmtNum(kr.valore_attuale, kr.unita)} / {fmtNum(kr.target_committed, kr.unita)}</span>
      <Pencil size={10} strokeWidth={1.5} className="opacity-0 group-hover/edit:opacity-60 transition-opacity" />
    </button>
  );
}

/**
 * ObiettiviGroup — un gruppo "area" della lista obiettivi.
 * Collassabile via click sul titolo. Mantiene tutti i data-testid originali.
 *
 * Props:
 *   - area         : chiave area (es. 'business')
 *   - items        : array di obiettivi della stessa area
 *   - isCollapsed  : boolean
 *   - onToggle()   : callback toggle collapse
 */
export default function ObiettiviGroup({ area, items, isCollapsed, onToggle, onKrValueChange }) {
  const meta = AREE[area];
  if (!meta) return null;

  return (
    <div data-testid={`area-group-${area}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-baseline gap-4 mb-6 cursor-pointer group focus:outline-none"
        data-testid={`area-toggle-${area}`}
        aria-expanded={!isCollapsed}
        aria-controls={`area-list-${area}`}
      >
        <span className="shrink-0" style={{ color: meta.color }}>
          {isCollapsed
            ? <ChevronRight size={14} strokeWidth={1.5} />
            : <ChevronDown size={14} strokeWidth={1.5} />}
        </span>
        <span
          className="font-mono text-xs uppercase tracking-[0.2em] group-hover:opacity-80"
          style={{ color: meta.color }}
        >
          {meta.label}
        </span>
        <div className="flex-1 h-px" style={{ backgroundColor: meta.color, opacity: 0.25 }} />
        <span className="font-mono text-xs text-mute shrink-0">{items.length}</span>
      </button>

      <div
        id={`area-list-${area}`}
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{ maxHeight: isCollapsed ? 0 : 4000, opacity: isCollapsed ? 0 : 1 }}
      >
        <ul className="divide-y divide-line border-b border-line">
          {items.map((o) => (
            <li key={o.id} className="py-6" data-testid="objective-list-item">
              <Link
                to={`/obiettivo/${o.id}`}
                className="block group hover:bg-linesoft/60 -mx-3 px-3 py-2 transition-colors"
                data-testid={`objective-link-${o.id}`}
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-mono text-sm text-mute w-8">{fmtPad(o.ordine)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3
                        className="font-display text-xl md:text-2xl tracking-tight mb-1 group-hover:underline decoration-1 underline-offset-4"
                        style={{ color: meta.color }}
                      >
                        {o.titolo}
                      </h3>
                      <ArrowRight
                        size={14}
                        strokeWidth={1.5}
                        className="opacity-0 group-hover:opacity-60 transition-opacity"
                        style={{ color: meta.color }}
                      />
                    </div>
                    {o.perche && (
                      <p className="font-body text-sm text-soft leading-relaxed max-w-3xl">
                        {o.perche}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex-1 max-w-md">
                        <div className="h-[2px] bg-line relative overflow-hidden">
                          <div
                            className="h-full transition-[width] duration-700 ease-out"
                            style={{ width: `${o.progress}%`, backgroundColor: meta.color }}
                          />
                        </div>
                      </div>
                      <span className="font-mono text-xs text-soft">
                        {Math.round(o.progress)}% · {o.kr_count} KR
                      </span>
                    </div>
                    {o.key_results?.length > 0 && (
                      <div className="mt-4 space-y-1.5">
                        {o.key_results.map((k) => (
                          <div key={k.id} className="flex items-baseline gap-4 font-mono text-xs">
                            <span className="text-mute uppercase tracking-[0.15em]">
                              {k.natura || 'kr'}
                            </span>
                            <span className="text-ink flex-1 truncate font-body text-sm">
                              {k.titolo}
                            </span>
                            <InlineKrValueEditor kr={k} onSave={onKrValueChange} meta={meta} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
